/**
 * `projectBinaryCache.ts` — persistent bytes for the big, immutable objects
 * under `account/{customerId}/projectdata/`, so a page reload or a project
 * switch does not re-download something already on this machine.
 *
 * Two consumers, both large and both read many times:
 *   - **model assets** — `GltfAssetCache` reads the GLB / `.gltfzip` here
 *     before it reaches S3. Its own map holds DECODED three.js scenes and is
 *     cleared on project unload (decoded scenes are large); the bytes must
 *     survive that clear, which is the point. Bytes never scenes: a `GLTF` is
 *     not serializable, and the download is where the minutes go — parse and
 *     Draco decode still run on every load.
 *   - **workflow media** — `MediaLibraryModel.resolveUrlAsync` reads a
 *     `campfire://media/…` video or image here, and `prefetchAsync` fills it
 *     ahead of a guided run so playback never waits on the network.
 *
 * Keys are `customerId|objectKey`, which is the same space both callers
 * already address (`{guid}.glb`, `media/{name}-{suffix}.mp4`), so they share
 * one bucket, one budget and one LRU with no chance of collision.
 *
 * **⚠ No revalidation, because these objects are WRITE-ONCE — re-check this
 * before adding any S3 write path.** Every path that changes a model mints a
 * NEW object key: an import writes a fresh GUID (`ProjectModelUpload`), a
 * Save-to-Library FORK copies to a new asset id
 * (`ProjectModelSaveToLibrary.copyAssetBinary`; the in-place branch
 * deliberately never rewrites the GLB), and Pixyz writes its converted output
 * to a new GUID. A media upload gets a random suffix for the same reason
 * (`uniqueMediaFileName` — two people uploading `clip.mp4` must not overwrite
 * each other). Nothing overwrites an existing key, so a hit needs no ETag
 * compare and no `HeadObject` round trip. A writer that overwrote an object in
 * place would break this SILENTLY, serving stale bytes forever; the key would
 * then need a version term.
 *
 * **Cache Storage rather than IndexedDB** because the payload is already an
 * HTTP body, and because `AppCacheModel.clearCacheStorage` deletes every cache
 * bucket — so app settings' "Clear Cache" covers this for free. The only thing
 * that clear must additionally do is drop the index below.
 *
 * The store is ADVISORY in both directions and every failure is swallowed: a
 * miss, a quota refusal, a browser that evicted us under pressure, a private
 * window with no storage at all. The network is always the fallback, so
 * nothing here may ever fail a load.
 */

/** Bytes and content type of a cached object. */
export interface CachedObjectBytes {
  readonly blob: Blob;
  readonly contentType: string | null;
}

const K_CACHE_NAME = "campfire-project-binaries-v1";

/**
 * Advisory metadata for eviction: `{ [cacheKey]: { size, at } }`, where `at`
 * is a last-used epoch-ms stamp. Cache Storage exposes neither a size nor an
 * access time, so an LRU has to be kept alongside — in `localStorage` because
 * a size/timestamp row is tiny and reading it must not be async on the hot
 * path. It can drift (another tab, a browser eviction, a cleared profile), so
 * it is reconciled against the real bucket whenever accuracy matters — see
 * {@link reconcileIndex}.
 */
const K_INDEX_KEY = "Campfire.BinaryCache.Index";
const K_INDEX_VERSION = 1;

/**
 * User-facing kill switch — app settings' "Disable caching" toggle
 * (2026-08-27, user-requested as an on-device diagnostic for the iOS crash
 * investigation). Per-BROWSER, deliberately: a phone may want the cache off
 * while desktop keeps it, so this is a `localStorage` preference and not a
 * key in the account-wide `app-settings.json`. Absence = enabled (stock).
 *
 * Disabled gates the three READ/WRITE entry points only. The maintenance
 * surface — {@link dropCachedObjectBytes}, {@link clearProjectBinaryCacheIndex},
 * {@link cachedObjectBytesTotal} — keeps working, so "Clear Cache" can still
 * empty a bucket that was filled before the switch was thrown. Existing
 * entries are NOT deleted by disabling; re-enabling gets their hits back.
 */
const K_DISABLED_KEY = "Campfire.BinaryCache.Disabled";

export function isProjectBinaryCacheDisabled(): boolean {
  try {
    return window.localStorage.getItem(K_DISABLED_KEY) === "1";
  } catch {
    // Private mode / storage refusal: stock behavior.
    return false;
  }
}

export function setProjectBinaryCacheDisabled(disabled: boolean): void {
  try {
    if (disabled) {
      window.localStorage.setItem(K_DISABLED_KEY, "1");
    } else {
      window.localStorage.removeItem(K_DISABLED_KEY);
    }
  } catch {
    // Private mode / storage refusal — nothing to store, and reads already
    // degrade to enabled.
  }
}

/**
 * Hard ceiling regardless of how much the browser would allow. Scene GLBs run
 * to hundreds of MB, so this is a few large scenes or many small ones — enough
 * to make revisiting a project free without quietly taking a double-digit
 * share of the user's disk.
 */
const K_MAX_BYTES = 1_500 * 1024 * 1024;

/**
 * Share of the browser's own quota we are willing to occupy. Chrome typically
 * offers a large fraction of free disk (so {@link K_MAX_BYTES} binds); Safari
 * offers far less (so this one does), and leaving half the quota free keeps
 * the app's other stores — settings, thumbnails, prompts — from being evicted
 * to make room for a model.
 */
const K_QUOTA_FRACTION = 0.5;

interface IndexEntry {
  readonly size: number;
  /** Last-used epoch ms; `0` for an entry recovered without a stamp. */
  readonly at: number;
}

type AssetCacheIndex = Record<string, IndexEntry>;

/**
 * Keys read or written since page load. These are NEVER evicted to make room
 * for a new write, which is what stops a scene whose assets do not all fit
 * from thrashing — evicting A to store B and B to store A on every load, so
 * every load pays a full disk write and no load ever gets a hit. When room
 * cannot be made without touching one of these, the write is simply skipped.
 */
const s_touchedThisSession = new Set<string>();

/**
 * Writes are serialized: two concurrent placements would otherwise evict
 * against each other's stale view of the index and of the bucket.
 */
let s_writeChain: Promise<void> = Promise.resolve();

/**
 * Cache Storage is a secure-context API, so `caches` is genuinely `undefined`
 * on the plain-http LAN origin the port supports for device testing. Everything
 * degrades to "no cache" there rather than throwing.
 */
function isAvailable(): boolean {
  return typeof globalThis.caches !== "undefined";
}

function cacheKeyFor(customerId: string, objectKey: string): string {
  // Cache Storage requires an http(s) key. `.invalid` is reserved and cannot
  // resolve, which is the point: this URL is a key and is never fetched.
  return `https://campfire-asset-cache.invalid/${encodeURIComponent(customerId)}/${encodeURIComponent(objectKey)}`;
}

/**
 * Cached bytes for an asset object, or `null` for any miss — including a
 * browser that will not give us a cache at all.
 *
 * Reading does NOT report transfer progress, deliberately: a hit has no bytes
 * to move over the network, and `SceneEntityRenderer` settles the placement's
 * download share when the load returns. See the progress note on
 * `GltfAssetCache.getOrLoad`.
 */
/**
 * Is this object already stored? Cheap by design — `cache.match` hands back a
 * Response whose body is a lazy stream, so nothing is read. The prefetch asks
 * this before downloading, and must NOT use {@link readCachedObjectBytes},
 * which materializes a whole video to answer a yes/no question.
 */
export async function hasCachedObjectBytes(
  customerId: string,
  objectKey: string,
): Promise<boolean> {
  if (!isAvailable() || isProjectBinaryCacheDisabled() || !customerId || !objectKey) return false;
  try {
    const cache = await caches.open(K_CACHE_NAME);
    return (await cache.match(cacheKeyFor(customerId, objectKey))) !== undefined;
  } catch {
    return false;
  }
}

export async function readCachedObjectBytes(
  customerId: string,
  objectKey: string,
  signal?: AbortSignal,
): Promise<CachedObjectBytes | null> {
  if (!isAvailable() || isProjectBinaryCacheDisabled() || !customerId || !objectKey) return null;
  if (signal?.aborted) return null;
  const key = cacheKeyFor(customerId, objectKey);
  try {
    const cache = await caches.open(K_CACHE_NAME);
    const hit = await cache.match(key);
    if (!hit) {
      // The index claimed an entry the bucket does not have (browser eviction,
      // another tab's clear). Drop the row so it stops counting against the
      // budget.
      forgetIndexEntry(key);
      return null;
    }
    const blob = await hit.blob();
    if (signal?.aborted) return null;
    touchIndexEntry(key, blob.size);
    return { blob, contentType: hit.headers.get("content-type") };
  } catch (err) {
    console.warn(`[projectBinaryCache] read failed for ${objectKey}:`, err);
    return null;
  }
}

/**
 * Store an asset object's bytes. Fire-and-forget by contract — the caller
 * already has the asset, and a cache write must never delay a render or fail a
 * load.
 *
 * Call this only AFTER the bytes have decoded successfully, so a truncated or
 * corrupt download can never be persisted.
 */
export function writeCachedObjectBytes(
  customerId: string,
  objectKey: string,
  blob: Blob,
  contentType: string | null,
): void {
  // Gated HERE rather than in `writeAsync`, so a disabled cache never even
  // enqueues — no `caches.open`, no quota estimate, no eviction pass.
  if (!isAvailable() || isProjectBinaryCacheDisabled() || !customerId || !objectKey) return;
  if (blob.size <= 0) return;
  s_writeChain = s_writeChain
    .then(() => writeAsync(cacheKeyFor(customerId, objectKey), blob, contentType))
    .catch((err) => {
      console.warn(`[projectBinaryCache] write failed for ${objectKey}:`, err);
    });
}

/**
 * Forget one entry — used when cached bytes fail to decode, which is the one
 * way a hit can be worse than a miss. Without it the user would be stuck with
 * a broken model until they cleared the whole cache by hand.
 */
export async function dropCachedObjectBytes(
  customerId: string,
  objectKey: string,
): Promise<void> {
  if (!isAvailable() || !customerId || !objectKey) return;
  const key = cacheKeyFor(customerId, objectKey);
  try {
    const cache = await caches.open(K_CACHE_NAME);
    await cache.delete(key);
  } catch {
    // Non-fatal: the index row goes either way, so the entry stops being
    // trusted even if the bucket kept it.
  }
  forgetIndexEntry(key);
  s_touchedThisSession.delete(key);
}

/**
 * Drop the LRU index. `AppCacheModel.clearCacheAsync` deletes every Cache
 * Storage bucket, which takes the bytes; this keeps the index from surviving
 * them and mis-sizing the next eviction pass.
 */
export function clearProjectBinaryCacheIndex(): void {
  s_touchedThisSession.clear();
  try {
    window.localStorage.removeItem(K_INDEX_KEY);
  } catch {
    // Private mode / storage disabled.
  }
}

/** Bytes this cache believes it is holding — advisory, for diagnostics. */
export function cachedObjectBytesTotal(): number {
  let total = 0;
  for (const entry of Object.values(readIndex())) total += entry.size;
  return total;
}

async function writeAsync(
  key: string,
  blob: Blob,
  contentType: string | null,
): Promise<void> {
  const cache = await caches.open(K_CACHE_NAME);
  const budget = await budgetBytes();
  // A single asset larger than the whole budget can never be cached. This is
  // not merely a fast path: without it `evictToFit` would delete perfectly
  // good entries one by one, discover the incoming asset still does not fit,
  // and refuse the write anyway — destroying the cache for nothing.
  if (blob.size > budget) return;
  if (!(await evictToFit(cache, key, blob.size, budget))) return;

  if (!(await putEntry(cache, key, blob, contentType))) {
    // Almost always QuotaExceededError: the browser's real limit is below what
    // `estimate()` advertised, or another origin store grew. Free everything
    // this session has not touched and try once more.
    await evictAll(cache, key, { keepTouched: true });
    if (!(await putEntry(cache, key, blob, contentType))) {
      forgetIndexEntry(key);
      return;
    }
  }
  touchIndexEntry(key, blob.size);
}

async function putEntry(
  cache: Cache,
  key: string,
  blob: Blob,
  contentType: string | null,
): Promise<boolean> {
  try {
    await cache.put(
      key,
      new Response(blob, {
        headers: {
          "content-type": contentType ?? (blob.type || "application/octet-stream"),
          // Our own header, not `content-length`: it is what lets
          // `reconcileIndex` re-size an entry whose index row was lost.
          "x-cf-bytes": String(blob.size),
        },
      }),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Evict least-recently-used entries until `incomingSize` fits. Returns whether
 * it does — `false` means the write should be skipped, which happens when the
 * only remaining candidates are entries this session is using.
 */
async function evictToFit(
  cache: Cache,
  incomingKey: string,
  incomingSize: number,
  budget: number,
): Promise<boolean> {
  const index = await reconcileIndex(cache);
  let total = 0;
  for (const [key, entry] of Object.entries(index)) {
    if (key !== incomingKey) total += entry.size;
  }
  if (total + incomingSize <= budget) {
    writeIndex(index);
    return true;
  }

  const candidates = Object.entries(index)
    .filter(([key]) => key !== incomingKey && !s_touchedThisSession.has(key))
    .sort((a, b) => a[1].at - b[1].at);

  for (const [key, entry] of candidates) {
    if (total + incomingSize <= budget) break;
    try {
      await cache.delete(key);
    } catch {
      // Leave the row in place if the delete failed, so the bytes keep
      // counting against the budget rather than becoming invisible.
      continue;
    }
    delete index[key];
    total -= entry.size;
  }

  writeIndex(index);
  return total + incomingSize <= budget;
}

async function evictAll(
  cache: Cache,
  incomingKey: string,
  options: { keepTouched: boolean },
): Promise<void> {
  const index = await reconcileIndex(cache);
  for (const key of Object.keys(index)) {
    if (key === incomingKey) continue;
    if (options.keepTouched && s_touchedThisSession.has(key)) continue;
    try {
      await cache.delete(key);
      delete index[key];
    } catch {
      // Keep the row; the bytes are still there.
    }
  }
  writeIndex(index);
}

/**
 * Bring the index back in line with the bucket: drop rows whose entry is gone
 * (the browser evicted it), and add rows for entries the index never heard of
 * (another tab, a cleared profile), sized from the header we wrote. An entry
 * recovered this way gets `at: 0`, i.e. evicted first — which is the right
 * default for something we cannot date.
 */
async function reconcileIndex(cache: Cache): Promise<AssetCacheIndex> {
  const index = readIndex();
  let requests: readonly Request[];
  try {
    requests = await cache.keys();
  } catch {
    return index;
  }

  const live = new Set<string>();
  for (const request of requests) {
    live.add(request.url);
    if (index[request.url]) continue;
    let size = 0;
    try {
      const hit = await cache.match(request);
      const declared = Number.parseInt(hit?.headers.get("x-cf-bytes") ?? "", 10);
      if (Number.isFinite(declared) && declared > 0) size = declared;
    } catch {
      // Unsized: it still exists, so keep the row (at size 0 it simply does
      // not defend itself against eviction).
    }
    index[request.url] = { size, at: 0 };
  }
  for (const key of Object.keys(index)) {
    if (!live.has(key)) delete index[key];
  }
  return index;
}

async function budgetBytes(): Promise<number> {
  if (!navigator.storage?.estimate) return K_MAX_BYTES;
  try {
    const { quota } = await navigator.storage.estimate();
    if (typeof quota !== "number" || !Number.isFinite(quota) || quota <= 0) {
      return K_MAX_BYTES;
    }
    return Math.min(K_MAX_BYTES, Math.floor(quota * K_QUOTA_FRACTION));
  } catch {
    return K_MAX_BYTES;
  }
}

function readIndex(): AssetCacheIndex {
  try {
    const raw = window.localStorage.getItem(K_INDEX_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { v?: number; e?: unknown };
    if (parsed?.v !== K_INDEX_VERSION || typeof parsed.e !== "object" || parsed.e === null) {
      return {};
    }
    const out: AssetCacheIndex = {};
    for (const [key, value] of Object.entries(parsed.e as Record<string, unknown>)) {
      const entry = value as { size?: unknown; at?: unknown };
      const size = typeof entry?.size === "number" && entry.size >= 0 ? entry.size : 0;
      const at = typeof entry?.at === "number" && entry.at >= 0 ? entry.at : 0;
      out[key] = { size, at };
    }
    return out;
  } catch {
    // Unparseable or unavailable: an empty index is safe — it under-reports,
    // so `reconcileIndex` rebuilds it from the bucket on the next write.
    return {};
  }
}

function writeIndex(index: AssetCacheIndex): void {
  try {
    window.localStorage.setItem(
      K_INDEX_KEY,
      JSON.stringify({ v: K_INDEX_VERSION, e: index }),
    );
  } catch {
    // Private mode, or localStorage itself over quota. The cache still works;
    // it just loses its LRU ordering until the next successful write.
  }
}

function touchIndexEntry(key: string, size: number): void {
  s_touchedThisSession.add(key);
  const index = readIndex();
  index[key] = { size, at: Date.now() };
  writeIndex(index);
}

function forgetIndexEntry(key: string): void {
  const index = readIndex();
  if (!(key in index)) return;
  delete index[key];
  writeIndex(index);
}
